import React, {  useState } from "react";
import { RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import AccordionList from "src/components/AccordionList";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import List from "src/components/List";
import Container from "src/components/List/styles";
import Loader from "src/components/Loader";
import SortableHandle from "src/components/Sortable/Handle";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import { CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER } from "src/components/Accordion";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import WrapText from "src/components/WrapText";
import { IListOfQuestions, IMdtba004 } from "../../../interface";

const ListOfQuestions: React.FC<IListOfQuestions> = ({
	questionData,
	refModalAddChangeDeleteQuestion,
	refModalAddChangeDeleteAnswer,
	onCallbackDeleteQuestion,
	onCallbackAddOrChangeQuestion,
	permissions
}) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();

	const [loading, setLoading] = useState(false);
	const [expandedAccordion, setExpandedAccordion] = useState(false);
	const [ data, setData ] = useState<IMdtba004[] | null>(null);
	const [ tempData , setTempData ] = useState<IMdtba004[] | null>(null);
	const [waitingForResponse, setWaitingForResponse] = useState(false);
  	
	const loadData = async () => {
		try{
			setLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "DiagnosticModels/ListAnswers",
				urlParameters: {
					id: questionData.mda003Id
				}
			});

			setData(response.data);
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setLoading(false);
		}
	};

	const handleAccordion = (expanded:boolean) => {
		setExpandedAccordion(expanded);
		if(!expanded)
			return;
		loadData();
	};

	const onCallbackAddOrChange = () => {
		loadData();
	};

	const onCallbackDelete = (id: number) => {
		setData((prevState) => prevState && prevState.filter((item) => item.mda004Id !== id));
	};


	const options = (row: any) => (
		<>
			{permissions > 0 && (
				<RCCGrid
					container 
					item
					justifyContent="flex-end"
					xs={12}
					className={CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER}
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						{permissions >= 2 && (				
							<RCCTooltip
								text={translate("Adicionar Resposta")}
								color="purple"
							>
								<RCCButton
									icon={{name: "plus", size: 12}}
									outlined
									color="purple"
									size="xs"
									layout="isRounded"
									onClick={(e) => {e.stopPropagation(); refModalAddChangeDeleteAnswer.current?.actionButtonAdd(row.mda003Id, row.mda003Questao , onCallbackAddOrChange);}}
								/>
							</RCCTooltip>
						)}													
					</CheckPermission>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Editar Questão")}
							color="grey"
						>
							<RCCButton
								icon={{name: "pencil", size: 12}}
								outlined
								color="grey"
								size="xs"
								layout="isRounded"
								onClick={(e) => {e.stopPropagation(); refModalAddChangeDeleteQuestion.current?.actionButtonChange(row.mda003Id, onCallbackAddOrChangeQuestion);}}
							/>
						</RCCTooltip>													
					</CheckPermission>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						{permissions >= 2 && (
							<RCCTooltip
								text={translate("Excluir Questão")}
								color="red"
							>
								<RCCButton
									icon={{name: "trash", size: 12}}
									outlined
									color="red"
									size="xs"
									layout="isRounded"
									onClick={(e) => {e.stopPropagation(); refModalAddChangeDeleteQuestion.current?.actionButtonDelete(row, onCallbackDeleteQuestion);}}
								/>
							</RCCTooltip>
						)}													
					</CheckPermission>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<SortableHandle>
							<RCCTooltip 
								color="grey"
								text={translate("Mover Questão")}
							>
								<RCCButton
									color="grey"
									outlined
									icon={{
										name: "arrows"
									}}
									layout="isRounded"
									onClick={(e) => e.stopPropagation()}
								/>
							</RCCTooltip>	
						</SortableHandle>													
					</CheckPermission>													
				</RCCGrid>	
			)}
		</>
	);

	const queryReorderAnswers = async (criteria: any[]) => {
		setWaitingForResponse(true);
		try{
			await RCCServicesApi.putRequest({
				method: "DiagnosticModels/UpdateOrderAnswers",
				body: {
					rows: criteria,
					mda003Id: questionData.mda003Id
				}
			});
			setWaitingForResponse(false);
			
			addToast({
				color: "green",
				description: translate("Resposta movida com sucesso"),
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			setData(tempData);
			standardErrorMessages(err, translate("Erro ao mover resposta"), addToast);
			setWaitingForResponse(false);
			return false;
		}
	};


	return (
		<>
    	<RCCGrid className="accordion-businessModel">
				<AccordionList 
					expanded={expandedAccordion}
					showExpandIcon={false}
					onChange={(e, expanded) => {e.stopPropagation(); handleAccordion(expanded);}}
					header={
						<>
							<List 
								data={[questionData]}
								columns={[{
									idColumn: "mda003Questao",
									nameColumn: "Questão",
									xs: 10,
									justify: "left",
									truncate: true,
									cellRenderer: (row: any) => <WrapText lineClamp={2}>{row.mda003Questao}</WrapText>
								},{
									idColumn: "options",
									nameColumn: "options",
									xs: 2,
									justify: "right",
									truncate: false,
									cellRenderer: options
								}]}
								layout="table"
							/>
						</>
					}
				>
					<>
						{
							loading ? (
								<Loader />
							) : (
								<>
									{!data || data.length === 0 ? 
										(
											<RCCGrid 
												container 
												styleInline="margin: 10px"
											>
												<RCCAlert 
													title={translate("Atenção")}
													description={translate("Nenhuma resposta encontrada")}
												/>
											</RCCGrid>
										) : (
											<>
												<div className="container-actions">
													<RCCGrid 
														container
														item
														xs={12}
													>
														<ul className="ul-businessModel-head">
															<li className="businessModel-head">
																<RCCGrid 
																	container
																	item
																	justifyContent="space-between"
																	alignItems="center"
																	xs={12}
																>
																	<span className="head-level-1">{translate("Respostas")}</span>
																</RCCGrid>
															</li>
														</ul>
													</RCCGrid>

													<SortableContainer
														onDragStart={() => {
															setTempData(data);
														}}
  													onDragEnd={(activeIdX, overId) => {
															if(activeIdX === overId)
																return; 	

															const activeIndex = data.findIndex((checks) => checks.id === activeIdX);
															const overIndex = data.findIndex((checks) => checks.id === overId);

															const orderedItems = arrayMove(data, activeIndex, overIndex);

															setData(orderedItems);

															const rowsOrdered: any[] = [];

															orderedItems.forEach((item: any, index) => {
																const row = {
																	mda004Id: item.mda004Id,
																	mda004Ordem: index
																};
																rowsOrdered.push(row);
															});

															queryReorderAnswers(rowsOrdered);
														}}
														items={data.map((line) => ({
															...line,
															id: `${line.mda004Id.toString()}`
														}))}
														modifiers={[restrictToVerticalAxis]}
  												>
														<Container
															layout="table"
														>
															{data?.map((row: IMdtba004) => (
																<SortableItem 
																	id={row.mda004Id.toString()} 
																	key={`container-answer-${row.mda004Id}`} 
																  disabled={waitingForResponse}
																>
																	<li className="list-component-child">
																		<RCCGrid											
																			item
																			container
																			alignItems="center"
																			xs={12}
																			sm={9}
																			justifyContent="flex-start"
																		>
																			<WrapText>{row.mda004Resposta}</WrapText>											
																		</RCCGrid>
																		{permissions > 0 && (
																			<RCCGrid											
																				item
																				container
																				alignItems="center"
																				xs={12}
																				sm={3}
																				justifyContent="flex-end"
																			>
																				<SortableHandle>
																					<RCCTooltip 
																						color="purple"
																						text={translate("Mover Resposta")}
																					>
																						<RCCButton
																							color="transparent"
																							icon={{
																								name: "arrows",
																								color: "var(--grey)"
																							}}
																							layout="isRounded"
																							onClick={(e) => e.stopPropagation()}
																						/>
																					</RCCTooltip>	
																				</SortableHandle>
																				<RCCGrid styleInline="display: flex">
																					<OptionsColumn useStopPropagation>
																						<RCCDropDownItem
																							selfEnclose
																							onClick={(e) => {e.stopPropagation(); refModalAddChangeDeleteAnswer.current?.actionButtonChange(row, onCallbackAddOrChange);}}
																						>
																							<RCCIcon name="pencil" />
																							{translate("Alterar")}
																						</RCCDropDownItem>
																						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
																							{permissions >= 2 && (
																								<RCCDropDownItem 
																									selfEnclose
																									onClick={(e) => { e.stopPropagation(); refModalAddChangeDeleteAnswer.current?.actionButtonDelete(row, onCallbackDelete); }}
																								>
																									<RCCIcon name="trash" />
																									{translate("Excluir")}
																								</RCCDropDownItem>
																							)}
																						</CheckPermission>
																					</OptionsColumn>
																				</RCCGrid>
																			</RCCGrid>
																		)}	
																	</li>
																</SortableItem>
															))}
														</Container>
													</SortableContainer>
												</div>
															
											</>
										)}
								</>
							)
						}
					</>
				</AccordionList>
			</RCCGrid>	
		</>
	);
};


export default ListOfQuestions;
